import { useTranslator } from '@bluebeam/react-i18n';
import LoadingAnimation from 'components/LoadingAnimation';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
  StdListHeaderCheckbox,
} from 'components/StdList';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import Immutable from 'immutable';
import React, { CSSProperties, FC, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ConfirmUploadsRenderer from './ConfirmUploadsRenderer';

interface Props extends RouteComponentProps, WithAppStoreProps {
  location: {
    pathname: string;
    hash: string;
    search: string;
    state: {
      files: File[];
    };
  };
}

interface RenderingProps {
  style: CSSProperties;
  index: number;
  data: {
    id: string;
    type: string;
    name: string;
  }[];
}

interface StyleProps {
  height: number;
  width: number;
}

const ConfirmUploads: FC<Props> = ({
  location,
  appState,
  history,
  actions,
}) => {
  const { [FeatureFlag.ZipUploadsEnabled]: isZipUploadsEnabled } =
    useFeatureFlags();
  const fileInputRef: React.RefObject<HTMLInputElement> = React.createRef();
  const accept = isZipUploadsEnabled ? '.pdf,.zip' : '.pdf';
  const itemHeight = 64;
  const { translate } = useTranslator([
    'confirmUploads',
    'buttons',
    'toastText',
  ]);

  const [selectedFiles, setSelectedFiles] = useState(Immutable.Set());
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [files, setFiles] = useState(location?.state?.files || undefined);

  useEffect(() => {
    if (!files) {
      history.push(`/project/${appState.selectedProject}/drawings/upload`);
    }
  }, []);

  const handleCancelClick = (): void => {
    history.push(`/project/${appState.selectedProject}/drawings`);
  };

  const handleSelectMoreClick = (): void => {
    const fileInput = fileInputRef.current;

    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileSelect = (): void => {
    const fileInput = fileInputRef.current;

    if (fileInput && fileInput.files) {
      const filesUploaded = [...Array.from(fileInput.files)];

      if (filesUploaded) {
        const filesToDisplay = Array.from(files).concat(
          Array.from(filesUploaded)
        );
        setFiles(filesToDisplay);
      }
    }
  };

  const handleItemSelected = (name: string, isChecked: boolean): void => {
    const updatedFiles = isChecked
      ? selectedFiles.add(name)
      : selectedFiles.delete(name);
    setSelectedFiles(updatedFiles);
  };

  const handleCheckAll = (checked: boolean): void => {
    const allFiles = Array.from(files).map((file) => file.name);
    const updatedFiles = checked ? Immutable.Set(allFiles) : Immutable.Set();
    setSelectedFiles(updatedFiles);
  };

  const handleConfirmUploadClick = async (): Promise<void> => {
    const filesToUpload = Array.from(files).filter((file) => {
      return selectedFiles.has(file.name) && file.type === 'application/pdf';
    });

    if (filesToUpload.length !== 0) {
      try {
        setUploadingDocs(true);

        const unsupportedFiles = filesToUpload.length !== selectedFiles.size;
        const toastText = {
          drawingsUploaded: {
            title: translate('toastText:drawingsUploaded.title'),
            message: translate('toastText:drawingsUploaded.message'),
          },
        };

        await actions.uploadDocuments(
          appState.selectedProject,
          filesToUpload,
          toastText
        );
        history.push({
          pathname: `/project/${appState.selectedProject}/drawings/uploads`,
          state: { unsupportedFiles },
        });

        window.setTimeout(actions.getProcessingStatuses, 1000);
      } catch (e) {
        toast.error(
          translate('toastText:fileUploadError.message'),
          translate('toastText:fileUploadError.title')
        );
        setUploadingDocs(false);
      }
    }
  };

  const getParsedFiles = () => {
    const fileList: { id: string; type: string; name: string }[] = [];
    if (files) {
      Array.from(files).forEach((file, i) => {
        const fileWithId = {
          name: file.name,
          type: file.type,
          id: i.toString(),
        };
        fileList.push(fileWithId);
      });
    }
    return fileList;
  };

  return files ? (
    <>
      <div className="row items-center justify-between p4">
        <div className="row items-center">
          <div className="font-size-5 medium mr3">
            {translate('confirmUploads:filesToUpload', {
              count: files.length,
            })}
          </div>
          <button
            className="btn-sm btn-outline-primary"
            onClick={handleSelectMoreClick}
          >
            {translate('buttons:selectMore')}
          </button>
          <input
            id="confirm-uploads-select-more-input"
            type="file"
            accept={accept}
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            hidden
          />
        </div>
        <div>
          <button className="btn-sm btn-link mr3" onClick={handleCancelClick}>
            {translate('buttons:cancel')}
          </button>
          <button
            className="btn-sm btn-primary"
            disabled={selectedFiles.size === 0}
            onClick={handleConfirmUploadClick}
          >
            {translate('buttons:confirmUpload')}
          </button>
        </div>
      </div>
      <div className="h100 px4 py0" style={{ height: 'calc(100% - 100px)' }}>
        <StdList id="confirm-uploads-list" gridTemplateColumns="50px auto">
          <StdListHeader>
            <StdListHeaderCheckbox
              id="confirm-uploads-select-all-checkbox"
              indeterminate={false}
              checked={
                files.length !== 0 && files.length === selectedFiles.size
              }
              onChange={handleCheckAll}
            />
            <StdListHeaderCell text={translate('confirmUploads:name')} />
          </StdListHeader>
          <StdListBody>
            {uploadingDocs ? (
              <div
                className="flex-center"
                style={{
                  height: 'calc(100vh - 250px)',
                }}
              >
                <div className="column items-center">
                  <LoadingAnimation
                    pageName={translate('confirmUploads:loadingDrawings')}
                  />
                </div>
              </div>
            ) : (
              <AutoSizer>
                {({ height, width }: StyleProps) => (
                  <FixedSizeList
                    height={height}
                    width={width}
                    itemCount={files.length}
                    itemSize={itemHeight}
                    itemData={getParsedFiles()}
                  >
                    {({ style, index, data }: RenderingProps) =>
                      ConfirmUploadsRenderer(
                        style,
                        index,
                        data,
                        selectedFiles,
                        handleItemSelected
                      )
                    }
                  </FixedSizeList>
                )}
              </AutoSizer>
            )}
          </StdListBody>
        </StdList>
      </div>
    </>
  ) : null;
};

export default withRouter(withAppStore(ConfirmUploads));
